import { useState, useCallback, useEffect, Fragment } from 'react';
import { searchBookingHistory } from '../lib/api';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { MagnifyingGlass, CreditCard, User as UserIcon, FileText, Calendar, ArrowsClockwise, DownloadSimple, Copy } from '@phosphor-icons/react';
import { formatDateTime } from '../lib/utils';
import { toast } from 'sonner';
import LoginPasswordWidget from '../components/LoginPasswordWidget';

/**
 * BookingHistoryPage — 1-click search & audit for ALL booked rows.
 * - Type LPG ID / 10-digit mobile / customer name → instantly see kis sub-staff ne,
 *   kaunsa account+card use kar k, kaunsi file ka kaunsi row book ki.
 * - Filters: status, sub-staff (auto from results).
 * - Click on a row → expand karke poori audit detail (account email, card last4, time).
 */
export default function BookingHistoryPage() {
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [subStaff, setSubStaff] = useState('');
  const [agencyFilter, setAgencyFilter] = useState(null); // { type: 'distributor'|'agency'|'oil', value: string }
  const [agencyMulti, setAgencyMulti] = useState([]);     // multi-select: array of distributor names
  const [dateFrom, setDateFrom] = useState('');           // yyyy-mm-dd
  const [dateTo, setDateTo] = useState('');               // yyyy-mm-dd
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(null);

  const runSearch = useCallback(async () => {
    setLoading(true);
    try {
      const params = { q, limit: 100 };
      if (status) params.status = status;
      const { data } = await searchBookingHistory(params);
      setResults(data?.results || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Search fail hua');
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [q, status]);

  // Auto-run once on mount with empty query (shows recent submitted rows)
  useEffect(() => {
    runSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onKey = (e) => {
    if (e.key === 'Enter') runSearch();
  };

  const subStaffOptions = Array.from(new Set(results.map(r => r.sub_staff_name).filter(Boolean))).sort();
  // Agency dropdown options — derived from results so user picks from real data only.
  const agencyOptions = Array.from(new Set(results.map(r => r.distributor_name || r.booking_agency).filter(Boolean))).sort();
  let filtered = subStaff ? results.filter(r => r.sub_staff_name === subStaff) : results;
  if (agencyFilter) {
    const keyMap = { distributor: 'distributor_name', agency: 'booking_agency', oil: 'oil_company' };
    const key = keyMap[agencyFilter.type];
    filtered = filtered.filter(r => (r[key] || '').toLowerCase() === agencyFilter.value.toLowerCase());
  }
  // Multi-select agency filter — user often needs to see 2-3 agencies together.
  if (agencyMulti.length > 0) {
    const set = new Set(agencyMulti.map(a => a.toLowerCase()));
    filtered = filtered.filter(r => set.has((r.distributor_name || '').toLowerCase()));
  }
  // Date range filter — applied client-side on submitted_at (falls back to created_at).
  if (dateFrom) {
    const fromMs = new Date(dateFrom + 'T00:00:00').getTime();
    filtered = filtered.filter(r => {
      const ts = new Date(((r.submitted_at || r.created_at || '') + '').replace(/\s+/, 'T')).getTime();
      return ts >= fromMs;
    });
  }
  if (dateTo) {
    const toMs = new Date(dateTo + 'T23:59:59').getTime();
    filtered = filtered.filter(r => {
      const ts = new Date(((r.submitted_at || r.created_at || '') + '').replace(/\s+/, 'T')).getTime();
      return ts <= toMs;
    });
  }

  // Re-Login URL builder — opens vendor portal in new tab so staff/sub_staff
  // can quickly re-authenticate when an account complaint or audit comes up.
  const reLoginUrl = (platform) => {
    const p = (platform || '').toLowerCase();
    if (p.includes('amazon')) return 'https://www.amazon.in/ap/signin';
    if (p.includes('tata')) return 'https://www.tatadigital.com/login';
    if (p.includes('jio')) return 'https://www.jiomart.com/login';
    return 'https://www.google.com/search?q=' + encodeURIComponent(`${platform || 'account'} login`);
  };

  const exportCsv = () => {
    if (filtered.length === 0) { toast.error('Koi data nahi hai export karne ke liye'); return; }
    const csvEscape = v => {
      const s = (v === null || v === undefined) ? '' : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const headers = ['LPG ID', 'Customer', 'Mobile', 'Distributor', 'Booking Agency', 'Oil Company', 'Amount', 'Status', 'Sub-staff', 'Account Email', 'Account Platform', 'Card Last4', 'File Name', 'Booked At'];
    const rows = filtered.map(r => [
      r.consumer_id, r.customer_name, r.mobile,
      r.distributor_name, r.booking_agency, r.oil_company,
      r.amount, r.status, r.sub_staff_name,
      r.account_email, r.account_platform,
      r.card_last4, r.file_name, r.submitted_at || r.created_at,
    ]);
    const csv = [headers.map(csvEscape).join(','), ...rows.map(row => row.map(csvEscape).join(','))].join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const ts = new Date().toISOString().slice(0, 16).replace(/[:T]/g, '-');
    const suffix = agencyFilter ? `-${agencyFilter.value.replace(/\s+/g, '_').slice(0, 20)}` : '';
    a.download = `booking-history${suffix}-${ts}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success(`${filtered.length} rows exported`);
  };

  const exportColoredExcel = () => {
    if (filtered.length === 0) { toast.error('Koi data nahi hai export karne ke liye'); return; }
    const colorMap = {
      success: '#C6EFCE',  // green
      pending: '#FFEB9C',  // yellow
      failed: '#FFC7CE',   // red
      draft: '#EEEEEE',
    };
    const headers = ['LPG ID', 'Customer', 'Mobile', 'Distributor', 'Booking Agency', 'Oil Company', 'Amount', 'Status', 'Sub-staff', 'Account Email', 'Card Last4', 'File Name', 'Booked At'];
    let html = '<html><head><meta charset="utf-8"></head><body><table border="1">';
    html += '<tr style="background:#305496;color:#fff;font-weight:bold">' +
      headers.map(h => `<th>${h}</th>`).join('') + '</tr>';
    filtered.forEach(r => {
      const bg = colorMap[r.status] || '#ffffff';
      const cells = [r.consumer_id, r.customer_name, r.mobile,
        r.distributor_name, r.booking_agency, r.oil_company,
        r.amount, (r.status || '').toUpperCase(), r.sub_staff_name,
        r.account_email, r.card_last4 ? `****${r.card_last4}` : '',
        r.file_name, r.submitted_at || r.created_at];
      html += `<tr style="background:${bg}">` +
        cells.map(c => `<td>${c ?? ''}</td>`).join('') + '</tr>';
    });
    html += '</table></body></html>';
    const blob = new Blob([html], { type: 'application/vnd.ms-excel' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const ts = new Date().toISOString().slice(0, 16).replace(/[:T]/g, '-');
    const suffix = agencyFilter ? `-${agencyFilter.value.replace(/\s+/g, '_').slice(0, 20)}` : '';
    a.download = `booking-history${suffix}-${ts}.xls`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Excel downloaded (${filtered.length} rows, colored)`);
  };

  const statusBadge = (s) => {
    const map = {
      success: 'bg-emerald-100 text-emerald-700',
      pending: 'bg-amber-100 text-amber-700',
      failed: 'bg-red-100 text-red-700',
      draft: 'bg-gray-100 text-gray-600',
    };
    return <span className={`text-[10px] px-2 py-0.5 rounded font-bold ${map[s] || 'bg-gray-100 text-gray-600'}`}>{(s || 'draft').toUpperCase()}</span>;
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <MagnifyingGlass size={24} className="text-violet-600" weight="bold" />
            Booking History &amp; 1-Click Audit
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">
            LPG ID / Mobile / Naam type karo → 1 click me dekho kis account, kis card aur kaun sub-staff ne book kiya.
          </p>
        </div>
      </div>

      {/* Search bar */}
      <div className="card-flat p-4 space-y-3">
        <div className="flex gap-2 flex-wrap">
          <div className="flex-1 min-w-[260px]">
            <Input
              value={q}
              onChange={e => setQ(e.target.value)}
              onKeyDown={onKey}
              placeholder="Search: 17-digit LPG ID, 10-digit Mobile, Customer Name…"
              className="h-10 text-sm"
              data-testid="bh-search-input"
            />
          </div>
          <select
            value={status}
            onChange={e => setStatus(e.target.value)}
            className="h-10 text-sm rounded-md border border-gray-300 px-3"
            data-testid="bh-status-filter"
          >
            <option value="">All Status</option>
            <option value="success">Success</option>
            <option value="pending">Pending</option>
            <option value="failed">Failed</option>
            <option value="draft">Draft</option>
          </select>
          {subStaffOptions.length > 0 && (
            <select
              value={subStaff}
              onChange={e => setSubStaff(e.target.value)}
              className="h-10 text-sm rounded-md border border-gray-300 px-3"
              data-testid="bh-substaff-filter"
            >
              <option value="">All Sub-staff</option>
              {subStaffOptions.map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          )}
          {/* Agency multi-select dropdown — Feb 2026 user-fix.
              Users often filter bulk bookings across 2-3 agencies; a single-pick
              dropdown forced them to run 3 searches. This uses a native <select multiple>
              so they can Ctrl/Cmd-click multiple. */}
          {agencyOptions.length > 0 && (
            <div className="relative">
              <select
                multiple
                value={agencyMulti}
                onChange={e => setAgencyMulti(Array.from(e.target.selectedOptions).map(o => o.value))}
                className="h-10 text-xs rounded-md border border-amber-300 bg-amber-50 px-2 font-medium text-amber-900 min-w-[180px] max-w-[280px]"
                size={1}
                data-testid="bh-agency-multi"
                title="Ctrl/Cmd + click to select multiple agencies"
              >
                {agencyOptions.map(a => (
                  <option key={a} value={a}>📦 {a}</option>
                ))}
              </select>
              {agencyMulti.length > 0 && (
                <button
                  onClick={() => setAgencyMulti([])}
                  className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-4 h-4 text-[9px] flex items-center justify-center font-bold"
                  title="Clear"
                  data-testid="bh-clear-agency-multi"
                >×</button>
              )}
            </div>
          )}
          {/* Date range filters — from / to. User often audits a specific day's bookings. */}
          <input
            type="date"
            value={dateFrom}
            onChange={e => setDateFrom(e.target.value)}
            className="h-10 text-xs rounded-md border border-gray-300 px-2"
            placeholder="From"
            data-testid="bh-date-from"
            title="From date"
          />
          <input
            type="date"
            value={dateTo}
            onChange={e => setDateTo(e.target.value)}
            className="h-10 text-xs rounded-md border border-gray-300 px-2"
            placeholder="To"
            data-testid="bh-date-to"
            title="To date"
          />
          <Button onClick={runSearch} disabled={loading} className="h-10 bg-violet-600 hover:bg-violet-700" data-testid="bh-search-btn">
            <MagnifyingGlass size={16} className="mr-1" /> {loading ? 'Searching…' : 'Search'}
          </Button>
          <Button variant="outline" onClick={() => { setQ(''); setStatus(''); setSubStaff(''); setAgencyFilter(null); setAgencyMulti([]); setDateFrom(''); setDateTo(''); setTimeout(runSearch, 0); }} className="h-10" data-testid="bh-reset-btn">
            <ArrowsClockwise size={16} className="mr-1" /> Reset
          </Button>
          <Button variant="outline" onClick={exportCsv} disabled={filtered.length === 0} className="h-10 border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="bh-export-csv-btn">
            <DownloadSimple size={16} className="mr-1" /> Export CSV ({filtered.length})
          </Button>
          <Button onClick={exportColoredExcel} disabled={filtered.length === 0} className="h-10 bg-emerald-600 hover:bg-emerald-700" data-testid="bh-export-excel-btn" title="Coloured Excel: green=success, yellow=pending, red=failed">
            <DownloadSimple size={16} className="mr-1" /> Excel 🎨
          </Button>
        </div>
        <div className="text-xs text-gray-500 flex items-center gap-2 flex-wrap">
          {loading ? 'Loading…' : `${filtered.length} result${filtered.length === 1 ? '' : 's'}${q ? ` matching "${q}"` : ''}`}
          {agencyFilter && (
            <span className="inline-flex items-center gap-1 bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full text-[11px] font-semibold" data-testid="bh-active-agency-filter">
              {agencyFilter.type === 'distributor' && '📦'}
              {agencyFilter.type === 'agency' && '🏢'}
              {agencyFilter.type === 'oil' && '⛽'}
              {' '}{agencyFilter.value}
              <button onClick={() => setAgencyFilter(null)} className="ml-1 font-bold hover:text-red-700" data-testid="bh-clear-agency-filter" title="Clear agency filter">✕</button>
            </span>
          )}
        </div>
      </div>

      {/* Results table */}
      <div className="card-flat overflow-x-auto" data-testid="bh-results">
        <Table className="table-dense">
          <TableHeader>
            <TableRow>
              <TableHead className="w-8" />
              <TableHead>LPG ID</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>Mobile</TableHead>
              <TableHead>Agency</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Sub-staff</TableHead>
              <TableHead>Account</TableHead>
              <TableHead>Card</TableHead>
              <TableHead>Booked At</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 && !loading ? (
              <TableRow>
                <TableCell colSpan={11} className="text-center text-gray-500 py-8">
                  Koi booking nahi mili. Search box me LPG ID ya mobile type karo.
                </TableCell>
              </TableRow>
            ) : filtered.map(r => (
              <Fragment key={r.id}>
                <TableRow data-testid={`bh-row-${r.id}`} className="hover:bg-violet-50/50 cursor-pointer" onClick={() => setExpanded(expanded === r.id ? null : r.id)}>
                  <TableCell className="text-gray-400 text-xs">{expanded === r.id ? '▼' : '▶'}</TableCell>
                  <TableCell className="font-mono text-xs text-violet-700 font-bold">
                    {r.consumer_id ? (
                      <button
                        onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(r.consumer_id); toast.success(`LPG ID copied: ${r.consumer_id}`); }}
                        className="inline-flex items-center gap-1 hover:bg-violet-100 rounded px-1 py-0.5"
                        title="Click to copy LPG ID"
                        data-testid={`bh-copy-lpg-${r.id}`}
                      >
                        {r.consumer_id}
                        <Copy size={10} className="text-violet-400" />
                      </button>
                    ) : '-'}
                  </TableCell>
                  <TableCell className="text-sm">{r.customer_name || '-'}</TableCell>
                  <TableCell className="font-mono text-xs">
                    {r.mobile ? (
                      <button
                        onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(r.mobile); toast.success(`Mobile copied: ${r.mobile}`); }}
                        className="inline-flex items-center gap-1 hover:bg-blue-100 rounded px-1 py-0.5"
                        title="Click to copy mobile"
                        data-testid={`bh-copy-mobile-${r.id}`}
                      >
                        {r.mobile}
                        <Copy size={10} className="text-gray-400" />
                      </button>
                    ) : '-'}
                  </TableCell>
                  <TableCell className="text-xs">
                    {(r.distributor_name || r.booking_agency || r.oil_company) ? (
                      <div className="flex flex-col gap-0.5">
                        {r.distributor_name && (
                          <button
                            onClick={(e) => { e.stopPropagation(); setAgencyFilter({ type: 'distributor', value: r.distributor_name }); }}
                            className="inline-flex items-center gap-1 text-teal-800 bg-teal-50 hover:bg-teal-200 px-1.5 py-0.5 rounded text-[10px] font-semibold truncate max-w-[150px] transition-colors"
                            title={`Filter by distributor: ${r.distributor_name}`}
                            data-testid={`bh-filter-dist-${r.id}`}
                          >
                            📦 {r.distributor_name}
                          </button>
                        )}
                        {r.booking_agency && (
                          <button
                            onClick={(e) => { e.stopPropagation(); setAgencyFilter({ type: 'agency', value: r.booking_agency }); }}
                            className="inline-flex items-center gap-1 text-indigo-800 bg-indigo-50 hover:bg-indigo-200 px-1.5 py-0.5 rounded text-[10px] font-semibold truncate max-w-[150px] transition-colors"
                            title={`Filter by agency: ${r.booking_agency}`}
                          >
                            🏢 {r.booking_agency}
                          </button>
                        )}
                        {r.oil_company && (
                          <button
                            onClick={(e) => { e.stopPropagation(); setAgencyFilter({ type: 'oil', value: r.oil_company }); }}
                            className="text-[10px] text-gray-600 hover:text-gray-900 text-left"
                            title={`Filter by oil company: ${r.oil_company}`}
                          >
                            ⛽ {r.oil_company}
                          </button>
                        )}
                      </div>
                    ) : '-'}
                  </TableCell>
                  <TableCell className="text-right text-sm font-bold">₹{(r.amount || 0).toLocaleString('en-IN')}</TableCell>
                  <TableCell>{statusBadge(r.status)}</TableCell>
                  <TableCell className="text-sm text-gray-700">{r.sub_staff_name || '-'}</TableCell>
                  <TableCell className="text-xs">
                    {r.account_email ? (
                      <div className="space-y-0.5">
                        <div className="flex items-center gap-1 flex-wrap">
                          <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold ${(r.account_platform || '').toLowerCase().includes('amazon') ? 'bg-orange-50 text-orange-700' : 'bg-pink-50 text-pink-700'}`} title={`Platform: ${r.account_platform || '-'}`}>
                            {(r.account_platform || '').toLowerCase().includes('amazon') ? '🟠 AMZ' : '🔴 TATA'}
                          </span>
                          {/* Re-Login button — opens vendor portal in new tab so staff/sub_staff
                              can re-authenticate quickly when complaint comes for this account. */}
                          <a
                            href={reLoginUrl(r.account_platform)}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="inline-flex items-center gap-0.5 text-[10px] bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200 rounded px-1.5 py-0.5 font-bold"
                            title="Open vendor login page in new tab"
                            data-testid={`bh-relogin-${r.id}`}
                          >
                            🔓 Re-Login
                          </a>
                        </div>
                        <div
                          className="text-[11px] text-blue-700 truncate max-w-[180px] cursor-pointer hover:underline"
                          title={`Click to copy: ${r.account_email}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            navigator.clipboard.writeText(r.account_email);
                            toast.success(`Account copied: ${r.account_email}`);
                          }}
                        >
                          {r.account_email}
                        </div>
                        {/* Inline Login Password widget — password + live OTP merged.
                            One-click copy makes re-login flow truly 1-step. */}
                        {r.account_password && r.account_totp_secret ? (
                          <div onClick={(e) => e.stopPropagation()} className="pt-0.5">
                            <LoginPasswordWidget
                              password={r.account_password}
                              secret={r.account_totp_secret}
                              label={(r.account_platform || '').toLowerCase().includes('amazon') ? 'Amazon Login' : 'TataNeu Login'}
                              compact
                              testId={`bh-row-login-pwd-${r.id}`}
                            />
                          </div>
                        ) : r.account_password ? (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigator.clipboard.writeText(r.account_password);
                              toast.success('Password copied');
                            }}
                            className="text-[10px] font-mono bg-amber-50 text-amber-800 border border-amber-200 rounded px-1.5 py-0.5 hover:bg-amber-100"
                            title="Click to copy password"
                            data-testid={`bh-row-pwd-${r.id}`}
                          >
                            🔑 {r.account_password}
                          </button>
                        ) : null}
                      </div>
                    ) : '-'}
                  </TableCell>
                  <TableCell className="text-xs">
                    {r.card_last4 ? (
                      <span className="inline-flex items-center gap-1 text-fuchsia-700 bg-fuchsia-50 px-1.5 py-0.5 rounded font-mono">
                        <CreditCard size={11} weight="bold" /> ****{r.card_last4}
                      </span>
                    ) : '-'}
                  </TableCell>
                  <TableCell className="text-xs text-gray-500 whitespace-nowrap">
                    <Calendar size={11} className="inline mr-1" /> {formatDateTime(r.submitted_at || r.updated_at || r.created_at)}
                  </TableCell>
                </TableRow>
                {expanded === r.id && (
                  <TableRow key={`${r.id}-expand`} className="bg-violet-50/30" data-testid={`bh-expand-${r.id}`}>
                    <TableCell colSpan={11} className="text-xs text-gray-700 p-3">
                      {/* Platform-specific account block — Amazon shows Email+Password, TataNeu shows Account+Password */}
                      {r.account_email || r.account_password ? (() => {
                        const isAmz = (r.account_platform || '').toLowerCase().includes('amazon');
                        const palette = isAmz
                          ? { bg: 'from-orange-50 to-amber-50', border: 'border-orange-300', tag: 'bg-orange-600', label: '🟠 AMAZON ACCOUNT' }
                          : { bg: 'from-pink-50 to-rose-50', border: 'border-pink-300', tag: 'bg-pink-600', label: '🔴 TATANEU ACCOUNT' };
                        const copy = (val, lbl) => {
                          if (!val) return;
                          navigator.clipboard.writeText(val);
                          toast.success(`${lbl}: ${val.slice(0, 30)}${val.length > 30 ? '…' : ''}`);
                        };
                        return (
                          <div className={`mb-3 rounded-md bg-gradient-to-r ${palette.bg} border ${palette.border} p-2.5`}>
                            <div className="flex items-center gap-2 mb-1.5">
                              <span className={`text-[10px] font-bold text-white px-2 py-0.5 rounded ${palette.tag}`}>{palette.label}</span>
                              <span className="text-[10px] text-gray-500">click any field to copy</span>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-xs">
                              <div onClick={() => copy(r.account_email, isAmz ? 'Amazon Email' : 'TataNeu Account')} className="cursor-copy hover:bg-white/60 rounded px-1.5 py-1 transition-colors">
                                <div className="text-[10px] text-gray-500 uppercase tracking-wide">{isAmz ? 'Email' : 'Account'}</div>
                                <div className="font-mono text-blue-700 font-semibold break-all">{r.account_email || '-'}</div>
                              </div>
                              <div className="px-1.5 py-1">
                                <div className="text-[10px] text-gray-500 uppercase tracking-wide flex items-center gap-1">
                                  Login Password
                                  {r.account_totp_secret && <span className="text-[8px] bg-violet-100 text-violet-700 px-1 rounded">PWD + LIVE OTP</span>}
                                </div>
                                {r.account_totp_secret ? (
                                  <div className="mt-1 flex flex-col gap-1">
                                    <LoginPasswordWidget
                                      password={r.account_password}
                                      secret={r.account_totp_secret}
                                      label={isAmz ? 'Amazon Login (PWD 1)' : 'TataNeu Login'}
                                      compact
                                      testId={`bh-login-pwd-${r.id}`}
                                    />
                                    {r.account_password2 && (
                                      <LoginPasswordWidget
                                        password={r.account_password2}
                                        secret={r.account_totp_secret}
                                        label="Amazon Login (PWD 2)"
                                        compact
                                        testId={`bh-login-pwd2-${r.id}`}
                                      />
                                    )}
                                  </div>
                                ) : (
                                  <div onClick={() => copy(r.account_password, 'Password')} className="cursor-copy font-mono text-amber-700 font-bold break-all">
                                    {r.account_password || '-'}
                                  </div>
                                )}
                              </div>
                              <div onClick={() => copy(r.account_mobile, 'Mobile')} className="cursor-copy hover:bg-white/60 rounded px-1.5 py-1 transition-colors">
                                <div className="text-[10px] text-gray-500 uppercase tracking-wide">Mobile</div>
                                <div className="font-mono text-gray-800">{r.account_mobile || '-'}</div>
                              </div>
                            </div>
                            {r.card_last4 && (
                              <div className="mt-2 pt-2 border-t border-orange-200/50 grid grid-cols-1 md:grid-cols-3 gap-2 text-xs">
                                <div className="px-1.5 py-1">
                                  <div className="text-[10px] text-gray-500 uppercase tracking-wide">Card</div>
                                  <div className="font-mono text-fuchsia-700 font-bold">****{r.card_last4}</div>
                                </div>
                                <div className="px-1.5 py-1">
                                  <div className="text-[10px] text-gray-500 uppercase tracking-wide">Card Holder</div>
                                  <div>{r.card_holder || '-'}</div>
                                </div>
                                <div className="px-1.5 py-1">
                                  <div className="text-[10px] text-gray-500 uppercase tracking-wide">Vendor</div>
                                  <div>{r.card_vendor_name || '-'}</div>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })() : (
                        <div className="mb-3 rounded-md border border-dashed border-amber-300 bg-amber-50 p-2.5 text-center text-[11px] text-amber-800">
                          ℹ Account/Card details is row ke liye track nahi hue. Agar yeh freshly booked row hai to workspace me Account-switch confirm karke dobara submit karo. Purane rows ke liye aap is message ko ignore kar sakte ho.
                        </div>
                      )}

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <div><b>File:</b> <span className="text-gray-600 inline-flex items-center gap-1"><FileText size={11} /> {r.file_name || '-'}</span></div>
                        <div><b>Distributor:</b> {r.distributor_name || '-'}</div>
                        <div><b>Booking Agency:</b> {r.booking_agency || '-'}</div>
                        <div><b>OMC:</b> {r.oil_company || '-'}</div>
                        <div><b>Notes:</b> {r.notes || '-'}</div>
                        <div><b>Created:</b> {formatDateTime(r.created_at)}</div>
                      </div>
                      {r.extras && Object.keys(r.extras).length > 0 && (
                        <div className="mt-2 pt-2 border-t border-violet-200">
                          <div className="text-[10px] text-gray-500 font-bold mb-1">+EXTRAS FROM SOURCE FILE</div>
                          <div className="flex flex-wrap gap-1">
                            {Object.entries(r.extras).map(([k, v]) => (
                              <span key={k} className="text-[10px] bg-blue-50 text-blue-800 border border-blue-200 px-1.5 py-0.5 rounded">
                                <b>{k}:</b> {String(v)}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                )}
              </Fragment>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
